import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { useParams } from "react-router";
import {
	hasPermission as checkPermission,
	type Role,
} from "../lib/permissions";

type User = {
	id: string;
	email: string;
	name: string;
	role: Role | null;
	emailVerified: boolean;
	image: string | null;
	tosVersionAccepted: string | null;
	lastDataExportAt: number | null;
};

type AuthContextValue = {
	user: User | null;
	loading: boolean;
	workspaceStatus: number | null;
	workspaceName: string | null;
	checkSession: () => Promise<void>;
	signOut: () => Promise<void>;
	hasPermission: (resource: string, action: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const [workspaceStatus, setWorkspaceStatus] = useState<number | null>(null);
	const [workspaceName, setWorkspaceName] = useState<string | null>(null);

	const checkSession = useCallback(async () => {
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/me`, {
				credentials: "include",
			});
			if (!res.ok) {
				setUser(null);
				setWorkspaceStatus(res.status);
				return;
			}
			const data = (await res.json()) as {
				user: User;
				workspace?: { name: string };
			};
			setUser(data.user);
			setWorkspaceName(data.workspace?.name ?? null);
			setWorkspaceStatus(200);
		} catch {
			setUser(null);
			setWorkspaceStatus(null);
		} finally {
			setLoading(false);
		}
	}, [workspaceSlug]);

	useEffect(() => {
		checkSession();
	}, [checkSession]);

	const hasPermission = useCallback(
		(resource: string, action: string) =>
			checkPermission(user?.role, resource, action),
		[user?.role],
	);

	const signOut = useCallback(async () => {
		try {
			await fetch(`/api/tenant/${workspaceSlug}/auth/sign-out`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({}),
			});
		} catch {
			// proceed even on network error
		}
		setUser(null);
	}, [workspaceSlug]);

	return (
		<AuthContext.Provider
			value={{
				user,
				loading,
				workspaceStatus,
				workspaceName,
				checkSession,
				signOut,
				hasPermission,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth must be used within AuthProvider");
	return ctx;
}
